import { AfterViewInit, Component, EffectRef, effect, EnvironmentInjector, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthService, ParsedUser } from '@services/auth.service';
import { BehaviorSubject, combineLatest, filter, Observable, skip, Subscription, throttleTime } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import type { AppPlugin, URLOpenListenerEvent } from '@capacitor/app';
import { AppStateChangeService } from '@services/app-state-change.service';
import { environment } from '@environments/environment';
import hints from '@assets/jsons/tips.json';
import type SwiperType from 'swiper';
import { PlatformService } from '@services/platform.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { EventService } from '@services/event.service';
import { ConsoleErrorService } from '@services/console-error.service';
import { delay, distinctUntilChanged, map } from 'rxjs/operators';
import { UpdateAppService } from '@services/update-app.service';
import { MenuController, NavController } from '@ionic/angular';
import { NavigationStore } from '@services/navigation.store';
import { LocalStorageService } from '@services/local-storage.service';
import { MetaTagService } from '@avatar/services/meta-tag.service';
import { AvatarService } from '@avatar/services/avatar.service';
import { ChatsService } from '@services/chats.service';
import { SoundUnlockService } from '@avatar/services/sound-unlock.service';
import { FileResponse, QtyUnreadMessages } from '@api/client';
import { ModalStreamsService } from '@avatar/services/modal-streams.service';
import { ShareInboxService } from '@services/share-inbox.service';
import { DownloadQueueTaskView } from '@services/download-manager.interfaces';
import { DownloadManagerService } from '@services/download-manager.service';
import { ChatPresenceActivityService } from '@services/chat-presence-activity.service';

interface QuickMenuItem {
  id: string;
  labelKey: string;
  route?: string | null;
  iconSrc?: string;
  iconName?: string;
  exact: boolean;
  badgeType?: 'messages' | 'notifications';
}

interface QuickMenuGroup {
  id: string;
  items: QuickMenuItem[];
  standalone?: boolean;
}

interface DesktopInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<unknown>;
}

type MainMenuRoute = '/avatar' | '/chats' | '/calls';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly environmentInjector = inject(EnvironmentInjector);
  protected readonly router = inject(Router);
  protected readonly navController = inject(NavController);
  protected readonly platformService = inject(PlatformService);
  protected readonly notificationService = inject(NotificationService);
  protected readonly chatsService = inject(ChatsService);
  protected readonly updateAppService = inject(UpdateAppService);
  protected readonly avatarService = inject(AvatarService);
  protected readonly authService = inject(AuthService);
  protected readonly modalStreamsService = inject(ModalStreamsService);
  protected readonly shareInboxService = inject(ShareInboxService);
  protected readonly downloadManager = inject(DownloadManagerService);
  protected readonly isFooterCompact = this.modalStreamsService.getIsNearBottom();
  protected desktopInstallPromptAvailable: boolean = false;
  protected readonly downloadsProgressRingRadius: number = 20;
  protected readonly downloadsProgressRingCircumference: number = 2 * Math.PI * this.downloadsProgressRingRadius;
  private readonly menuController = inject(MenuController);
  private readonly translate = inject(TranslateService);
  private readonly eventService = inject(EventService);
  private readonly appStateChangeService = inject(AppStateChangeService);
  private readonly ngZone = inject(NgZone);
  private readonly consoleErrorService = inject(ConsoleErrorService);
  private readonly navigationStore = inject(NavigationStore);
  private readonly localStorage = inject(LocalStorageService);
  private readonly metaTagService = inject(MetaTagService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly chatPresenceActivityService = inject(ChatPresenceActivityService);

  private static readonly IOS_KEYBOARD_OVERLAP_CLASS_NAME: string = 'app-ios-keyboard-overlap';

  private showMenuBehaviorSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected showMenu$: Observable<boolean> = this.showMenuBehaviorSubject.asObservable();
  private lastURL: string = '';
  protected requestedUrl: string;
  protected availableTips: any[] = [];
  protected currentHint: number = null;
  protected isMainMenuOpen: boolean = false;
  protected parsedUser: ParsedUser | null = null;
  protected disableChatTransition: boolean = false;
  protected readonly drawerMenuGroups: QuickMenuGroup[] = [
    {
      id: 'streams',
      standalone: true,
      items: [
        {
          id: 'streams',
          labelKey: 'avatar.streamsModal.title',
          route: '/avatar',
          iconSrc: 'assets/app/icon/world.svg',
          exact: false,
        },
      ],
    },
    {
      id: 'profile-notifications',
      items: [
        {
          id: 'profile',
          labelKey: 'app.menu.profile',
          route: '/profile',
          iconSrc: 'assets/app/icon/profile.svg',
          exact: false,
        },
        {
          id: 'notifications',
          labelKey: 'app.menu.notifications',
          route: '/notifications',
          iconSrc: 'assets/app/icon/notifications.svg',
          exact: false,
          badgeType: 'notifications',
        },
      ],
    },
    {
      id: 'call-messenger-people',
      items: [
        {
          id: 'call',
          labelKey: 'app.menu.call',
          route: '/calls',
          iconName: 'call-outline',
          exact: false,
        },
        {
          id: 'messenger',
          labelKey: 'app.menu.chats',
          route: '/chats',
          iconSrc: 'assets/app/icon/chats.svg',
          exact: false,
          badgeType: 'messages',
        },
        {
          id: 'people',
          labelKey: 'app.menu.people',
          route: '/people',
          iconName: 'people-outline',
          exact: false,
        },
      ],
    },
    {
      id: 'freelance-wallet',
      items: [
        {
          id: 'freelance',
          labelKey: 'app.menu.tasks',
          route: '/tasks',
          iconSrc: 'assets/app/icon/briefcase.svg',
          exact: true,
        },
        {
          id: 'wallet',
          labelKey: 'app.menu.wallet',
          route: null,
          iconName: 'wallet-outline',
          exact: false,
        },
      ],
    },
    {
      id: 'support',
      standalone: true,
      items: [
        {
          id: 'ai-image-bot',
          labelKey: 'app.menu.aiImageBot',
          route: '/ai-image-bot',
          iconName: 'image-outline',
          exact: false,
        },
        {
          id: 'support',
          labelKey: 'app.menu.support',
          route: '/support',
          iconName: 'help-circle-outline',
          exact: false,
        },
      ],
    },
  ];
  private swiper: SwiperType | null = null;
  private swiperModulePromise: Promise<{
    swiper: typeof import('swiper');
    modules: typeof import('swiper/modules');
  }> | null = null;
  private swiperPluginsRegistered: boolean = false;
  private smartlookLoaded: boolean = false;
  private titleSub: Subscription = null;
  private descriptionSub: Subscription = null;
  private unreadNotificationsCount: number = 0;
  private unreadChatsCount: number = 0;
  private unreadTotalCount: number = 0;
  private unreadBadgeEffectRef: EffectRef | null = null;
  private titleMutationObserver: MutationObserver | null = null;
  private desktopUnreadResyncIntervalId: number | null = null;
  private desktopUnreadResyncInProgress: boolean = false;
  private readonly desktopUnreadResyncIntervalMs: number = 45_000;
  private faviconImage: HTMLImageElement | null = null;
  private faviconImageLoadPromise: Promise<HTMLImageElement | null> | null = null;
  private originalFaviconHref: string | null = null;
  private lastFaviconBadgeCount: number | null = null;
  private faviconBadgeRequestId: number = 0;
  private sb: SubscriptionsBag = new SubscriptionsBag();
  private capacitorApp: AppPlugin | null = null;
  private capacitorAppReady: Promise<void> | null = null;
  private desktopInstallPrompt: DesktopInstallPrompt | null = null;
  private readonly pendingInviteKey: string = 'pendingInviteToken';
  private readonly mainMenuRouteStorageKey: string = 'mainMenuRoute';
  private readonly chatDetailRoutePattern = /^\/chats\/\d+(?:\/|$)/;
  private readonly unreadTitlePrefixPattern: RegExp = /^\(\d+\)\s/;
  private readonly beforeInstallPromptListener = (event: Event): void => {
    if (!this.isDesktopInstallEnvironment()) return;

    const promptData: DesktopInstallPrompt | null = this.extractDesktopInstallPrompt(event);
    if (!promptData) return;

    event.preventDefault();
    this.desktopInstallPrompt = promptData;
    this.desktopInstallPromptAvailable = true;
  };
  private readonly appInstalledListener = (): void => {
    this.resetDesktopInstallPrompt();
  };
  private readonly desktopUnreadVisibilityChangeListener = (): void => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    void this.refreshDesktopUnreadCounts();
  };
  private readonly desktopUnreadWindowFocusListener = (): void => {
    void this.refreshDesktopUnreadCounts();
  };

  #soundUnlock = inject(SoundUnlockService);

  constructor() {
    if (this.platformService.isDevice) {
      void this.addDeepLinksListener();
    }
  }

  private get allowedShowDownloadPrompt(): boolean {
    const excludedParts: string[] = ['pyjam-animate', 'support', '.html', 'video-call', 'audio-call', 'incoming-call', 'consumer'];
    const hasExcludedPart: boolean = excludedParts.some((part: string): boolean => this.router.url.includes(part));
    return !hasExcludedPart;
  }

  async ngOnInit(): Promise<void> {
    this.subscribeToKeyboardSafeAreaBottom();
    this.chatPresenceActivityService.start();

    const lang: string = this.setDefaultLang();
    this.subscribeToEvents(lang);
    this.initDesktopInstallPrompt();
    this.initDesktopUnreadIndicators();

    if (this.platformService.isDevice) {
      this.runWhenIdle((): void => {
        void this.updateAppService.checkActualAppVersion();
      });
    }
    if (!this.platformService.isSSR
      && this.platformService.isAndroidWeb
      && this.allowedShowDownloadPrompt) {
      this.runWhenIdle((): void => {
        this.updateAppService.checkDownloadPromptPeriod();
      });
    }
  }

  private runWhenIdle(task: () => void): void {
    if (typeof window === 'undefined') {
      setTimeout((): void => task(), 0);
      return;
    }

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback((): void => task());
    } else {
      setTimeout((): void => task(), 0);
    }
  }

  ngAfterViewInit() {
    this.addingScripts();
  }

  async ngOnDestroy(): Promise<void> {
    this.setKeyboardSafeAreaBottomCollapsed(false);
    if (this.titleSub) {
      this.titleSub.unsubscribe();
      this.titleSub = null;
    }
    if (this.descriptionSub) {
      this.descriptionSub.unsubscribe();
      this.descriptionSub = null;
    }
    this.sb.unsubscribeAll();
    await this.ensureCapacitorApp();
    const app = this.capacitorApp;
    if (app) {
      await app.removeAllListeners();
    }
    this.destroyDesktopUnreadIndicators();
    this.detachDesktopInstallPromptListeners();
  }

  private setDefaultLang(): string {
    if (typeof navigator === 'undefined') return environment.defaultLanguage;

    const userLang: string = (navigator.language) ? navigator.language.split('-')[0] : environment.defaultLanguage;
    const lang: string = environment.activeLanguages.includes(userLang) ? userLang : environment.defaultLanguage;
    this.translate.setDefaultLang(lang);

    return lang;
  }

  private subscribeToKeyboardSafeAreaBottom(): void {
    if (!this.platformService.isIosApp) {
      this.setKeyboardSafeAreaBottomCollapsed(false);
      return;
    }

    this.sb.sub = combineLatest([
      this.platformService.isKeyboardVisible$,
      this.platformService.keyboardHeight$,
    ]).pipe(
      map(([isKeyboardVisible, keyboardHeight]: [boolean, number]): boolean => isKeyboardVisible && keyboardHeight > 0),
      distinctUntilChanged(),
    ).subscribe((hasKeyboardBottomOverlap: boolean): void => {
      this.setKeyboardSafeAreaBottomCollapsed(hasKeyboardBottomOverlap);
    });
  }

  private setKeyboardSafeAreaBottomCollapsed(value: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.classList.toggle(AppComponent.IOS_KEYBOARD_OVERLAP_CLASS_NAME, value);
  }

  private needSaveLastURL(urlAfterRedirects: string): boolean {
    const excludedParts: string[] = ['login', 'registration', '.html', 'video-call', 'audio-call', 'incoming-call', 'order-pay', 'consumer'];
    const hasExcludedPart: boolean = excludedParts.some((part: string): boolean => urlAfterRedirects.includes(part));
    return !hasExcludedPart;
  }

  private subscribeToEvents(lang: string): void {
    this.sb.sub = this.authService.parsedUser$.pipe(
      distinctUntilChanged((previousUser: ParsedUser, currentUser: ParsedUser): boolean => (previousUser?.email === currentUser?.email) && (previousUser?.phone === currentUser?.phone))
    ).subscribe(async (parsedUser: ParsedUser): Promise<void> => {
      this.parsedUser = parsedUser;
      if (!parsedUser) {
        this.translate.use(lang);
        return;
      }

      if (parsedUser?.lang?.locale && environment.activeLanguages.includes(parsedUser.lang.locale)) {
        this.translate.use(parsedUser.lang.locale);
      } else {
        this.translate.use(this.translate.defaultLang);
      }

      void this.checkUserProfileComplete();
      if (this.isWebUnreadIndicatorsEnvironment()) {
        void this.refreshDesktopUnreadCounts();
      } else {
        void this.notificationService.checkUnreadNotifications();
      }
      this.handlePendingInvite();
    });

    this.sb.sub = this.appStateChangeService.appStateChange$.pipe(
      skip(1),
      filter((isActive: boolean): boolean => isActive),
    ).subscribe((): void => {
      this.syncShowMenuWithCurrentRoute();
    });

    this.sb.sub = this.appStateChangeService.appStateChange$.pipe(
      skip(1),
      throttleTime(2_000),
      filter((isActive: boolean): boolean =>
        isActive && this.platformService.isDevice && this.authService.isAuthenticated
      ),
      delay(2_000),
    ).subscribe((): void => {
      void this.notificationService.checkUnreadNotifications(); // if not device, then update from socket event SOCKET_EVENT_NAMES.NOTIFICATION, because socket is available, on notification.service
    });

    this.sb.sub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        console.log('\x1b[34m' + '[NavigationStart] to url: ' + event.url + '\x1b[0m');

        if (this.lastURL === '/avatar' && event.url !== '/avatar') {
          this.eventService.closeModalsEvent.emit();
        }

        const fromUrl = this.normalizeRouteUrl(this.router.url);
        const toUrl = this.normalizeRouteUrl(event.url);
        this.disableChatTransition = this.isChatDetailRoute(fromUrl) || this.isChatDetailRoute(toUrl);
      }

      if (event instanceof NavigationStart && event.url.indexOf('/login') === -1) {
        this.requestedUrl = event.url;
      }

      if (event instanceof NavigationEnd) {
        console.log('\x1b[34m' + '[NavigationEnd] to url: ' + event.url + '\x1b[0m');

        this.lastURL = event.url;
      }

      if (!(event instanceof NavigationEnd)) return;

      const normalizedUrlAfterRedirects: string = this.normalizePath(event.urlAfterRedirects);
      this.saveLastMainMenuRoute(normalizedUrlAfterRedirects);

      const currentRoute: ActivatedRoute = this.getChildRoute(this.activatedRoute);
      const {title, description} = currentRoute.snapshot.data;
      this.titleSub?.unsubscribe();
      this.titleSub = this.translate.get(title || 'seo.title.default').subscribe(titleText => {
        this.metaTagService.updateTitle(titleText);
      });
      this.descriptionSub?.unsubscribe();
      this.descriptionSub = this.translate.get(description || 'seo.description.default').subscribe(descriptionText => {
        this.metaTagService.updateDescription(descriptionText);
      });

      this.syncShowMenuWithCurrentRoute(currentRoute);

      if (!this.localStorage.isAvailable) return;

      const urlAfterRedirects: string = event.urlAfterRedirects;

      if (this.needSaveLastURL(urlAfterRedirects)) {
        this.navigationStore.setLastUrl(urlAfterRedirects);
      }

      setTimeout((): void => {
        this.eventService.checkNewTipsSubject.next(true);
      }, 2500);
    });

    this.sb.sub = this.eventService.checkNewTipsSubject.subscribe((): void => {
      this.showTips();
    });
  }

  private normalizeRouteUrl(url: string): string {
    const queryIndex = url.indexOf('?');
    const hashIndex = url.indexOf('#');
    let endIndex = url.length;
    if (queryIndex >= 0) {
      endIndex = Math.min(endIndex, queryIndex);
    }
    if (hashIndex >= 0) {
      endIndex = Math.min(endIndex, hashIndex);
    }
    return url.slice(0, endIndex);
  }

  private normalizePath(url: string): string {
    const normalizedRoute: string = this.normalizeRouteUrl(url);
    if (!normalizedRoute || normalizedRoute === '/') {
      return '/';
    }
    return (normalizedRoute.length > 1 && normalizedRoute.endsWith('/'))
      ? normalizedRoute.slice(0, -1)
      : normalizedRoute;
  }

  private resolveMainMenuRoute(url: string): MainMenuRoute | null {
    const normalizedPath: string = this.normalizePath(url);

    if (normalizedPath === '/avatar' || normalizedPath.startsWith('/avatar/')) {
      return '/avatar';
    }
    if (normalizedPath === '/chats' || normalizedPath.startsWith('/chats/')) {
      return '/chats';
    }
    if (normalizedPath === '/calls' || normalizedPath.startsWith('/calls/')) {
      return '/calls';
    }
    return null;
  }

  private saveLastMainMenuRoute(url: string): void {
    if (!this.localStorage.isAvailable) {
      return;
    }
    const mainMenuRoute: MainMenuRoute | null = this.resolveMainMenuRoute(url);
    if (!mainMenuRoute) {
      return;
    }
    this.localStorage.setItem(this.mainMenuRouteStorageKey, mainMenuRoute);
  }

  private isChatDetailRoute(url: string): boolean {
    return this.chatDetailRoutePattern.test(url);
  }

  private handlePendingInvite(): void {
    const token: string | null = this.localStorage.getItem(this.pendingInviteKey);
    if (!token) return;
    if (this.router.url.startsWith('/invite/')) return;

    void this.router.navigate(['/invite', token]);
  }

  private getChildRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private syncShowMenuWithCurrentRoute(route: ActivatedRoute | null = null): void {
    const currentRoute: ActivatedRoute = route ?? this.getChildRoute(this.activatedRoute);
    const nextShowMenu: boolean = this.getShowMenuFromRoute(currentRoute);
    if (this.showMenuBehaviorSubject.value === nextShowMenu) {
      return;
    }
    this.showMenuBehaviorSubject.next(nextShowMenu);
  }

  private getShowMenuFromRoute(route: ActivatedRoute): boolean {
    const activeRoutes: ActivatedRoute[] = route.pathFromRoot;
    for (let index: number = activeRoutes.length - 1; index >= 0; index -= 1) {
      const showMenuCandidate: unknown = activeRoutes[index].snapshot.data['showMenu'];
      if (typeof showMenuCandidate === 'boolean') {
        return showMenuCandidate;
      }
    }
    return false;
  }

  private addingScripts(): void {
    //   if (environment.useFbSdk) {
    //     const fbScript: HTMLScriptElement = document.createElement('script');
    //     fbScript.innerHTML = `
    //       !function(f,b,e,v,n,t,s)
    //       {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    //       n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    //       if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    //       n.queue=[];t=b.createElement(e);t.async=!0;
    //       t.src=v;s=b.getElementsByTagName(e)[0];
    //       s.parentNode.insertBefore(t,s)}(window, document,'script',
    //       'https://connect.facebook.net/en_US/fbevents.js');
    //       fbq('init', '` + environment.fbTrId + `');
    //       fbq('track', 'PageView');
    //       `;
    //     document.querySelector('head').appendChild(fbScript);
    //
    //     const fbPixel: HTMLElement = document.createElement('noscript');
    //     const fbPixelImg: HTMLImageElement = document.createElement('img');
    //     fbPixelImg.setAttribute('src', 'https://www.facebook.com/tr?id=' + environment.fbTrId + '&ev=PageView&noscript=1');
    //     fbPixel.appendChild(fbPixelImg);
    //     document.querySelector('head').appendChild(fbPixel);
    //
    //     setTimeout(async () => {
    //       await FacebookLogin.initialize({appId: environment.fbAppId});
    //       // console.log('platforms:', getPlatforms());
    //       if (!this.platformService.isAndroid) {
    //         await FacebookLogin.setAdvertiserTrackingEnabled({enabled: true});
    //         await FacebookLogin.setAdvertiserIDCollectionEnabled({enabled: true});
    //       }
    //     });
    //   }

    //   if (environment.useYm) {
    //     const ymScript: HTMLScriptElement = document.createElement('script');
    //     ymScript.innerHTML = `
    //     (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
    //     m[i].l=1*new Date();
    //     for (var j = 0;
    //     j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
    //     k=e.createElement(t),
    //     a=e.getElementsByTagName(t)[0],
    //     k.async=1,k.src=r,
    //     a.parentNode.insertBefore(k,a)})
    //     (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
    //
    //     ym(` + environment.ymId + `, "init", {
    //         clickmap:true,
    //         trackLinks:true,
    //         accurateTrackBounce:true,
    //         webvisor:true
    //     });`;
    //     document.querySelector('head').appendChild(ymScript);
    //
    //     const ymPixel = document.createElement('noscript');
    //     const ymPixelImg = document.createElement('img');
    //     ymPixelImg.setAttribute('src', 'https://mc.yandex.ru/watch/' + environment.ymId);
    //     ymPixelImg.setAttribute('style', 'position:absolute; left:-9999px;');
    //     ymPixel.appendChild(ymPixelImg);
    //     document.querySelector('head').appendChild(ymPixel);
    //   }

    if (!environment.useSmartlook || (typeof document === 'undefined')) return;

    this.runWhenIdle((): void => {
      if (this.smartlookLoaded) return;
      this.smartlookLoaded = true;

      const script: HTMLScriptElement = document.createElement('script');
      script.innerHTML = `
        window.smartlook||(function(d) {
          var o=smartlook=function(){ o.api.push(arguments)},
          h=d.getElementsByTagName('head')[0];
          var c=d.createElement('script');
          o.api=new Array();c.async=true;
          c.type='text/javascript';
          c.charset='utf-8';
          c.src='https://web-sdk.smartlook.com/recorder.js';
          h.appendChild(c);
          })(document);
          smartlook('init','${environment.smartlookApiKey}', { region: 'eu' });
      `;

      document.head.appendChild(script);
    });
  }

  private async checkUserProfileComplete(): Promise<void> {
    if (this.authService.isUserProfileIncomplete) {
      await this.navController.navigateBack('/registration', {replaceUrl: true});
    }
  }

  protected async openMainMenu(): Promise<void> {
    await this.menuController.open('main-menu');
  }

  protected onDownloadsFabClick(): void {
    void this.openMainMenu();
  }

  protected async closeMainMenu(): Promise<void> {
    await this.menuController.close('main-menu');
  }

  protected get canShowDesktopInstallButton(): boolean {
    return this.desktopInstallPromptAvailable
      && this.isDesktopInstallEnvironment()
      && !this.isStandaloneDisplayMode();
  }

  protected async onDesktopInstallClick(): Promise<void> {
    if (!this.desktopInstallPrompt) return;

    try {
      await this.desktopInstallPrompt.prompt();
      const choiceResult: unknown = await this.desktopInstallPrompt.userChoice;
      const outcome: string | null = this.extractInstallOutcome(choiceResult);
      if (outcome) {
        console.log('Desktop install prompt outcome:', outcome);
      }
    } catch (error) {
      console.error('Desktop install prompt error', error);
    } finally {
      this.resetDesktopInstallPrompt();
    }
  }

  protected onMainMenuOpen(): void {
    this.isMainMenuOpen = true;
  }

  protected onMainMenuClose(): void {
    this.isMainMenuOpen = false;
  }

  protected navigateFromMenu(route: string): void {
    void this.closeMainMenu().finally((): void => {
      void this.navController.navigateForward(route);
    });
  }

  protected onQuickMenuItemClick(item: QuickMenuItem): void {
    if (!this.hasMenuRoute(item)) {
      return;
    }
    this.navigateFromMenu(item.route);
  }

  protected onCancelDownloadTask(taskId: string, event: Event): void {
    event.stopPropagation();
    this.downloadManager.cancelTask(taskId);
  }

  protected trackByDownloadTaskId(index: number, task: DownloadQueueTaskView): string {
    return task.id;
  }

  protected get downloadsProgressDashOffset(): number {
    const normalizedProgress: number = this.downloadManager.overallProgressPercent();
    const clampedProgress: number = Math.max(0, Math.min(100, normalizedProgress));
    return this.downloadsProgressRingCircumference * (1 - (clampedProgress / 100));
  }

  protected get downloadsProgressPercentLabel(): string {
    return `${this.downloadManager.overallProgressPercent()}%`;
  }

  protected hasMenuRoute(item: QuickMenuItem): item is QuickMenuItem & { route: string } {
    return typeof item.route === 'string' && item.route.trim().length > 0;
  }

  protected isMenuItemActive(item: QuickMenuItem): boolean {
    if (!this.hasMenuRoute(item)) {
      return false;
    }
    const route = item.route;
    if (item.id === 'streams') {
      const currentPath = this.getCurrentPath();
      return currentPath === '/avatar';
    }
    if (this.requestedUrl) {
      if (item.exact) {
        return this.requestedUrl === route;
      }
      if (this.requestedUrl.startsWith(route)) {
        return true;
      }
    }
    return this.router.isActive(route, item.exact);
  }

  private getCurrentPath(): string {
    const current = this.requestedUrl || this.router.url || '';
    const stripped = current.split('?')[0].split('#')[0];
    if (stripped.length > 1 && stripped.endsWith('/')) {
      return stripped.slice(0, -1);
    }
    return stripped;
  }

  protected get userFullName(): string {
    const user: ParsedUser | null = this.authService?.parsedUser;
    if (!user || (user && !user.name && !user.surname)) {
      return '';
    }
    const parts: string[] = [];
    if (user.name) {
      parts.push(user.name);
    }
    if (user.surname) {
      parts.push(user.surname);
    }
    return parts.join(' ');
  }

  protected get userAvatar(): FileResponse | null {
    return this.authService?.parsedUser?.avatar || null;
  }

  protected get userStatusLine(): string | null {
    const user: ParsedUser | null = this.parsedUser;
    if (!user) {
      return null;
    }
    if (user.description && user.description.trim().length > 0) {
      return user.description;
    }
    if (user.phone && user.phone.trim().length > 0) {
      return user.phone;
    }
    return null;
  }

  //#region Tips
  private async initializeSwiper(): Promise<void> {
    const tip = this.availableTips[this.currentHint];

    if (!tip || (typeof document === 'undefined')) return;

    const {swiper, modules} = await this.loadSwiperModule();
    const {default: SwiperCtor} = swiper;
    const {Navigation, EffectFade, Pagination} = modules;

    if (!this.swiperPluginsRegistered) {
      SwiperCtor.use([Pagination, EffectFade, Navigation]);
      this.swiperPluginsRegistered = true;
    }

    const hintEl: HTMLElement = document.querySelector('.hint');
    AppComponent.updateHint(hintEl, tip);

    this.swiper = new SwiperCtor(
      '.tips-swiper',
      {
        loop: false,
        initialSlide: 0,
        navigation: {
          nextEl: '.swiper-button-next,.next-tip',
          prevEl: '.swiper-button-prev',
        },
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        pagination: {
          el: '.swiper-pagination.tips-pagination',
          clickable: true,
          dynamicBullets: false,
        },
        on: {
          transitionStart: (swiper: SwiperType): void => {
            if (swiper?.wrapperEl) {
              swiper.wrapperEl.setAttribute('current', swiper.activeIndex.toString());
            }
          },
          realIndexChange: (swiper: SwiperType): void => {
            const currentIndex: number = parseInt(swiper.wrapperEl.getAttribute('current'), 10);

            if (swiper?.activeIndex !== currentIndex && typeof window !== 'undefined') {
              this.ngZone.run((): void => {
                this.currentHint = swiper.activeIndex;
                const currentTip = window['availableTips'][swiper.activeIndex];
                const hintElRef: HTMLElement = document.querySelector('.hint');

                AppComponent.updateHint(hintElRef, currentTip);
              });
            }
          },
        },
      },
    );
  }

  private loadSwiperModule(): Promise<{
    swiper: typeof import('swiper');
    modules: typeof import('swiper/modules');
  }> {
    if (!this.swiperModulePromise) {
      this.swiperModulePromise = Promise.all([
        import('swiper'),
        import('swiper/modules'),
      ]).then(([swiper, modules]) => ({swiper, modules}));
    }

    return this.swiperModulePromise;
  }

  private showTips(): void {
    setTimeout(() => {
      if (this.platformService.isSSR) return;

      const passedTips: string[] = (this.localStorage.getItem('passedTips'))
        ? this.localStorage.getItem('passedTips').split(',')
        : [];

      this.availableTips = hints.tips.filter(tip => {
        const target: Element = document.querySelector(tip.element);
        const targetRect: DOMRect = target?.getBoundingClientRect();
        return (targetRect?.width && targetRect?.height && (passedTips.indexOf(tip.name) == -1));
      });
      window['availableTips'] = this.availableTips;
      this.currentHint = 0;

      if (this.availableTips.length > 0) {
        void this.initializeSwiper().then((): void => {
          if (this.swiper?.slides.length === 0) {
            this.showTips();
          }
        });
      }
    }, 200);
  }

  protected onCloseHintClick(): void {
    this.currentHint = null;
    AppComponent.hideHintHighlights();
  }

  static updateHint(hintEl, tip, attempt = 0): void {
    if (typeof window === 'undefined') return;

    const targetEl = document.querySelector(tip.element);
    if (!hintEl || !targetEl) {
      if (attempt < 3) {
        console.error('No Hint or Target element!', tip);
        setTimeout((): void => {
          console.log('Try updateHint again!');
          const hintEl: HTMLElement = document.querySelector('.hint');
          this.updateHint(hintEl, tip, attempt + 1);
        }, 2000);
      }
      return;
    }

    hintEl.classList.remove('left-arrow');
    hintEl.classList.remove('right-arrow');
    hintEl.classList.remove('top-arrow');
    hintEl.classList.remove('bottom-arrow');

    const pageRect: DOMRect = document.querySelector('ion-app.ion-page').getBoundingClientRect();
    const hintRect = hintEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const targetLeft: number = targetRect.left - pageRect.left;
    const targetTop: number = targetRect.top;
    const targetX: number = targetLeft + targetRect.width / 2;
    const targetY: number = targetRect.top + targetRect.height / 2;
    const targetWidth: number = targetRect.width;
    const targetHeight: number = targetRect.height;
    const targetCenterDistX: number = Math.abs(targetX - pageRect.width / 2);
    const targetCenterDistY: number = Math.abs(targetY - window.innerHeight / 2);
    const borderDist: number = 10;
    let left: number = 0;
    let top: number = 0;

    if (targetCenterDistY > targetCenterDistX && targetY < window.innerHeight / 2) {
      left = targetX - hintRect.width / 2;
      top = targetTop + targetHeight + 15;
    } else if (targetCenterDistY > targetCenterDistX && targetY > window.innerHeight / 2) {
      left = targetX - hintRect.width / 2;
      top = targetTop - hintRect.height - 15;
    } else if (targetCenterDistY < targetCenterDistX && targetX < pageRect.width / 2) {
      left = targetLeft + targetWidth + 15;
      top = targetTop + targetHeight / 2 - hintRect.height / 2;
    } else {
      left = targetLeft - hintRect.width - 15;
      top = targetTop + targetHeight / 2 - hintRect.height / 2;
    }

    if (window.innerHeight - top - hintRect.height < borderDist) {
      top = window.innerHeight - hintRect.height - borderDist;
    }

    if (pageRect.width - left - hintRect.width < borderDist) {
      left = pageRect.width - hintRect.width - borderDist;
    }

    if (top < borderDist) {
      top = borderDist;
    }

    if (left < borderDist) {
      left = borderDist;
    }

    hintEl.style.left = left + 'px' || '';
    hintEl.style.top = top + 'px' || '';

    AppComponent.hideHintHighlights();
    targetEl.classList.add('hint-highlight');

    if (tip.round) {
      targetEl.style.borderRadius = tip.round;
    }

    setTimeout((): void => {
      const targetRect = targetEl.getBoundingClientRect();
      const targetX = targetRect.left + targetRect.width / 2;
      const targetY = targetRect.top + targetRect.height / 2;
      const hintRect = hintEl.getBoundingClientRect();

      if (targetY < hintRect.top && targetX > hintRect.left && targetX < hintRect.left + hintRect.width) {
        hintEl.classList.add('top-arrow');
        hintEl.style.setProperty('--hint-arrow-offset', (targetX - hintRect.left - 15) + 'px');
      } else if (targetX < hintRect.left && targetY > hintRect.top && targetY < hintRect.top + hintRect.height) {
        hintEl.classList.add('left-arrow');
        hintEl.style.setProperty('--hint-arrow-offset', (targetY - hintRect.top - 15) + 'px');
      } else if ((targetX > hintRect.left + hintRect.width) && targetY > hintRect.top && targetY < hintRect.top + hintRect.height) {
        hintEl.classList.add('right-arrow');
        hintEl.style.setProperty('--hint-arrow-offset', (targetY - hintRect.top - 15) + 'px');
      } else if ((targetY > hintRect.top + hintRect.height) && targetX > hintRect.left && targetX < hintRect.left + hintRect.width) {
        hintEl.classList.add('bottom-arrow');
        hintEl.style.setProperty('--hint-arrow-offset', (targetX - hintRect.left - 15) + 'px');
      }

      hintEl.style.opacity = 1;
      const storagePassedTips: string = localStorage.getItem('passedTips');
      const passedTips: string[] = (storagePassedTips) ? storagePassedTips.split(',') : [];

      if (passedTips.find((tipName: string): boolean => tipName == tip.name) === undefined) {
        passedTips.push(tip.name);
        localStorage.setItem('passedTips', passedTips.join(','));
      }
    });
  }

  static hideHintHighlights(): void {
    if (typeof window === 'undefined') return;

    const highlightedElems: NodeListOf<Element> = document.querySelectorAll('.hint-highlight');
    highlightedElems.forEach((element: HTMLElement): void => {
      element.classList.remove('hint-highlight');
      element.style.borderRadius = undefined;
    });
  }

  //#endregion Tips

  protected async openConsoleModal(): Promise<void> {
    await this.consoleErrorService.openConsoleModal();
  }

  private async addDeepLinksListener(): Promise<void> {
    if (typeof document === 'undefined') return;
    await this.ensureCapacitorApp();
    const app = this.capacitorApp;
    if (!app) {
      return;
    }

    app.addListener('appUrlOpen', async (event: URLOpenListenerEvent): Promise<void> => {
      await this.handleDeepLinkUrl(event.url);
    })
      // .then(() => console.log('Deep links listener added'))
      .catch((error) => console.error('Deep links listener error:', error));

    try {
      const launchUrlResult = await app.getLaunchUrl();
      const launchUrl: string | undefined = launchUrlResult?.url;
      if (launchUrl) {
        await this.handleDeepLinkUrl(launchUrl);
      }
    } catch (error) {
      console.error('Deep links launch URL error:', error);
    }
  }

  private async ensureCapacitorApp(): Promise<void> {
    if (this.platformService.isSSR) {
      return;
    }
    if (this.capacitorApp) {
      return;
    }
    if (!this.capacitorAppReady) {
      this.capacitorAppReady = import('@capacitor/app')
        .then(({ App }) => {
          this.capacitorApp = App;
        })
        .catch((error) => {
          console.error('Failed to load Capacitor App plugin', error);
        });
    }
    await this.capacitorAppReady;
  }

  private getDeepLinkSlug(url: string): string | null {
    if (!url) return null;
    try {
      const parsedUrl = new URL(url);
      const slug = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
      if (!slug || slug === '/') return null;
      return slug;
    } catch (error) {
      console.error('Deep link parse error', error);
      return null;
    }
  }

  private async handleDeepLinkUrl(url: string): Promise<void> {
    await this.ngZone.run(async (): Promise<void> => {
      const slug: string | null = this.getDeepLinkSlug(url);
      if (!slug) {
        console.warn('No slug in URL:', url);
        return;
      }

      const currentRoute: string = this.normalizeNavigationUrl(this.router.url);
      const targetRoute: string = this.normalizeNavigationUrl(slug);
      if (currentRoute === targetRoute) {
        return;
      }

      console.log('App opened with URL:', url);

      if (slug.startsWith('/avatar/vplay') || slug.startsWith('/avatar/consumer')) {
        console.log('Try to unlock sound....');
        await this.#soundUnlock.unlock();
      }

      await this.navController.navigateForward(slug, {animated: false, replaceUrl: true});
    });
  }

  private normalizeNavigationUrl(url: string): string {
    if (!url) return '/';
    return url.startsWith('/') ? url : `/${url}`;
  }

  private initDesktopUnreadIndicators(): void {
    if (!this.isWebUnreadIndicatorsEnvironment()) return;

    this.unreadBadgeEffectRef = effect(
      (): void => {
        this.unreadNotificationsCount = this.notificationService.countNotifications();
        this.applyDesktopUnreadIndicators();
      },
      { injector: this.environmentInjector }
    );

    this.sb.sub = this.chatsService.unreadMessages$.subscribe((value: QtyUnreadMessages): void => {
      this.unreadChatsCount = this.getUnreadChatsCount(value);
      this.applyDesktopUnreadIndicators();
    });

    this.startDesktopUnreadResync();
    if (this.authService.isAuthenticated) {
      void this.refreshDesktopUnreadCounts();
    }

    this.attachTitleMutationObserver();
    this.applyDesktopUnreadIndicators();
  }

  private destroyDesktopUnreadIndicators(): void {
    this.unreadBadgeEffectRef?.destroy();
    this.unreadBadgeEffectRef = null;

    if (this.titleMutationObserver) {
      this.titleMutationObserver.disconnect();
      this.titleMutationObserver = null;
    }

    this.stopDesktopUnreadResync();
    this.restoreOriginalFavicon();
    this.faviconImage = null;
    this.faviconImageLoadPromise = null;
  }

  private startDesktopUnreadResync(): void {
    if (!this.isWebUnreadIndicatorsEnvironment()) return;
    if (typeof window === 'undefined') return;
    if (typeof document === 'undefined') return;

    this.stopDesktopUnreadResync();

    window.addEventListener('focus', this.desktopUnreadWindowFocusListener);
    document.addEventListener('visibilitychange', this.desktopUnreadVisibilityChangeListener);

    this.desktopUnreadResyncIntervalId = window.setInterval((): void => {
      if (document.visibilityState !== 'visible') return;
      void this.refreshDesktopUnreadCounts();
    }, this.desktopUnreadResyncIntervalMs);
  }

  private stopDesktopUnreadResync(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.desktopUnreadWindowFocusListener);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.desktopUnreadVisibilityChangeListener);
    }
    if ((typeof window !== 'undefined') && (this.desktopUnreadResyncIntervalId !== null)) {
      window.clearInterval(this.desktopUnreadResyncIntervalId);
    }

    this.desktopUnreadResyncIntervalId = null;
    this.desktopUnreadResyncInProgress = false;
  }

  private async refreshDesktopUnreadCounts(): Promise<void> {
    if (!this.isWebUnreadIndicatorsEnvironment()) return;
    if (!this.authService.isAuthenticated) return;
    if (this.desktopUnreadResyncInProgress) return;

    this.desktopUnreadResyncInProgress = true;
    try {
      await Promise.all([
        this.notificationService.checkUnreadNotifications(),
        this.chatsService.checkUnreadMessages(),
      ]);
    } finally {
      this.desktopUnreadResyncInProgress = false;
    }
  }

  private attachTitleMutationObserver(): void {
    if (typeof document === 'undefined') return;
    if (typeof MutationObserver === 'undefined') return;

    const titleElement: HTMLTitleElement | null = document.querySelector('title');
    if (!titleElement) return;

    this.titleMutationObserver?.disconnect();
    this.titleMutationObserver = new MutationObserver((): void => {
      this.updateDocumentTitleWithUnreadCount(this.unreadTotalCount);
    });

    this.titleMutationObserver.observe(titleElement, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  private applyDesktopUnreadIndicators(): void {
    const unreadTotal: number = this.unreadNotificationsCount + this.unreadChatsCount;
    this.unreadTotalCount = unreadTotal;
    this.updateDocumentTitleWithUnreadCount(unreadTotal);
    void this.updateFaviconBadge(unreadTotal);
    void this.updateDesktopAppBadge(unreadTotal);
  }

  private getUnreadChatsCount(value: QtyUnreadMessages | null | undefined): number {
    const done: number = value?.done || 0;
    const connects: number = value?.connects || 0;
    const inProgress: number = value?.inProgress || 0;
    return done + connects + inProgress;
  }

  private updateDocumentTitleWithUnreadCount(unreadCount: number): void {
    if (typeof document === 'undefined') return;

    const currentTitle: string = document.title || 'Pyjam App';
    const titleWithoutUnreadPrefix: string = this.stripUnreadPrefix(currentTitle);
    const nextTitle: string = unreadCount > 0
      ? `(${unreadCount}) ${titleWithoutUnreadPrefix}`
      : titleWithoutUnreadPrefix;

    if (nextTitle !== currentTitle) {
      document.title = nextTitle;
    }
  }

  private stripUnreadPrefix(title: string): string {
    return title.replace(this.unreadTitlePrefixPattern, '');
  }

  private async updateDesktopAppBadge(unreadCount: number): Promise<void> {
    if (typeof navigator === 'undefined') return;

    const setAppBadgeCandidate: unknown = Reflect.get(navigator, 'setAppBadge');
    const clearAppBadgeCandidate: unknown = Reflect.get(navigator, 'clearAppBadge');

    try {
      if (unreadCount > 0) {
        if (typeof setAppBadgeCandidate === 'function') {
          await Promise.resolve(Reflect.apply(setAppBadgeCandidate, navigator, [unreadCount]));
        }
        return;
      }

      if (typeof clearAppBadgeCandidate === 'function') {
        await Promise.resolve(Reflect.apply(clearAppBadgeCandidate, navigator, []));
      }
    } catch (error) {
      if (!environment.production) {
        console.warn('Desktop badge API error', error);
      }
    }
  }

  private async updateFaviconBadge(unreadCount: number): Promise<void> {
    if (!this.isWebUnreadIndicatorsEnvironment()) return;
    if (typeof document === 'undefined') return;

    const faviconLink: HTMLLinkElement | null = this.getFaviconLink();
    if (!faviconLink) return;
    this.captureOriginalFaviconHref(faviconLink);

    const normalizedUnreadCount: number = Math.max(0, Math.floor(unreadCount));
    if (normalizedUnreadCount <= 0) {
      this.restoreOriginalFavicon();
      return;
    }

    if (this.lastFaviconBadgeCount === normalizedUnreadCount) {
      return;
    }

    const requestId: number = ++this.faviconBadgeRequestId;
    const faviconImage: HTMLImageElement | null = await this.loadBaseFaviconImage();
    if (!faviconImage || requestId !== this.faviconBadgeRequestId) return;

    const iconSize: number = 64;
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = iconSize;
    canvas.height = iconSize;
    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(faviconImage, 0, 0, iconSize, iconSize);
    this.drawFaviconBadge(context, iconSize, normalizedUnreadCount);

    faviconLink.setAttribute('type', 'image/png');
    faviconLink.setAttribute('href', canvas.toDataURL('image/png'));
    this.lastFaviconBadgeCount = normalizedUnreadCount;
  }

  private drawFaviconBadge(context: CanvasRenderingContext2D, iconSize: number, unreadCount: number): void {
    const label: string = unreadCount > 99 ? '99+' : `${unreadCount}`;
    const badgeRadius: number = label.length > 2 ? 16 : 14;
    const centerX: number = iconSize - badgeRadius;
    const centerY: number = badgeRadius;

    context.fillStyle = '#FF3B30';
    context.beginPath();
    context.arc(centerX, centerY, badgeRadius, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = '#FFFFFF';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = label.length > 2 ? '700 16px sans-serif' : '700 18px sans-serif';
    context.fillText(label, centerX, centerY + 0.5);
  }

  private getFaviconLink(): HTMLLinkElement | null {
    if (typeof document === 'undefined') return null;

    const iconLink: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
    if (iconLink) return iconLink;

    return document.querySelector('link[rel="shortcut icon"]');
  }

  private captureOriginalFaviconHref(faviconLink: HTMLLinkElement): void {
    if (this.originalFaviconHref) return;

    const hrefAttribute: string | null = faviconLink.getAttribute('href');
    this.originalFaviconHref = (typeof hrefAttribute === 'string' && hrefAttribute.length > 0)
      ? hrefAttribute
      : faviconLink.href;
  }

  private restoreOriginalFavicon(): void {
    if (typeof document === 'undefined') return;
    if (!this.originalFaviconHref) return;

    const faviconLink: HTMLLinkElement | null = this.getFaviconLink();
    if (!faviconLink) return;

    this.faviconBadgeRequestId += 1;
    faviconLink.setAttribute('href', this.originalFaviconHref);
    this.lastFaviconBadgeCount = null;
  }

  private async loadBaseFaviconImage(): Promise<HTMLImageElement | null> {
    if (this.faviconImage) return this.faviconImage;
    if (this.faviconImageLoadPromise) return this.faviconImageLoadPromise;

    const source: string | null = this.resolveFaviconSource();
    if (!source) return null;

    this.faviconImageLoadPromise = new Promise<HTMLImageElement | null>((resolve) => {
      const image: HTMLImageElement = new Image();
      image.onload = (): void => {
        this.faviconImage = image;
        resolve(image);
      };
      image.onerror = (): void => resolve(null);
      image.src = source;
    });

    const loadedImage: HTMLImageElement | null = await this.faviconImageLoadPromise;
    this.faviconImageLoadPromise = null;
    return loadedImage;
  }

  private resolveFaviconSource(): string | null {
    if (!this.originalFaviconHref) return null;

    try {
      return new URL(this.originalFaviconHref, document.baseURI).href;
    } catch {
      return this.originalFaviconHref;
    }
  }

  private initDesktopInstallPrompt(): void {
    if (typeof window === 'undefined') return;
    if (!this.isDesktopInstallEnvironment()) return;
    if (this.isStandaloneDisplayMode()) return;

    window.addEventListener('beforeinstallprompt', this.beforeInstallPromptListener);
    window.addEventListener('appinstalled', this.appInstalledListener);
  }

  private detachDesktopInstallPromptListeners(): void {
    if (typeof window === 'undefined') return;

    window.removeEventListener('beforeinstallprompt', this.beforeInstallPromptListener);
    window.removeEventListener('appinstalled', this.appInstalledListener);
  }

  private extractDesktopInstallPrompt(event: Event): DesktopInstallPrompt | null {
    const promptCandidate: unknown = Reflect.get(event, 'prompt');
    const userChoiceCandidate: unknown = Reflect.get(event, 'userChoice');

    if (typeof promptCandidate !== 'function') return null;
    if (!(userChoiceCandidate instanceof Promise)) return null;

    return {
      prompt: (): Promise<void> => Promise.resolve(Reflect.apply(promptCandidate, event, [])).then((): void => undefined),
      userChoice: userChoiceCandidate,
    };
  }

  private extractInstallOutcome(choice: unknown): string | null {
    if (!choice || typeof choice !== 'object') return null;

    const outcome: unknown = Reflect.get(choice, 'outcome');
    return (typeof outcome === 'string') ? outcome : null;
  }

  private isDesktopInstallEnvironment(): boolean {
    return !this.platformService.isSSR
      && this.platformService.isDesktop
      && !this.platformService.isDevice
      && !this.platformService.isMobile;
  }

  private isWebUnreadIndicatorsEnvironment(): boolean {
    return !this.platformService.isSSR
      && this.platformService.isBrowser
      && !this.platformService.isDevice;
  }

  private isStandaloneDisplayMode(): boolean {
    if (typeof window === 'undefined') return false;

    const isStandalone: boolean = window.matchMedia('(display-mode: standalone)').matches;
    const isWindowControlsOverlay: boolean = window.matchMedia('(display-mode: window-controls-overlay)').matches;
    const isNavigatorStandalone: boolean = Reflect.get(window.navigator, 'standalone') === true;
    return isStandalone || isWindowControlsOverlay || isNavigatorStandalone;
  }

  private resetDesktopInstallPrompt(): void {
    this.desktopInstallPrompt = null;
    this.desktopInstallPromptAvailable = false;
  }
}
